import { useId } from 'react';

import { PeriodType } from '@/lib/api/generated/model';
import { PERIOD_LABEL } from '@/features/workspace-usage';

export interface PeriodFieldProps {
  value: PeriodType;
  onChange: (next: PeriodType) => void;
  disabled?: boolean;
}

const OPTIONS = [
  PeriodType.daily,
  PeriodType.weekly,
  PeriodType.monthly,
  PeriodType.none,
] as const;

/**
 * How often a limit renews (spec 091 FR-001, FR-003).
 *
 * A radio group of visually-hidden inputs behind visible label pills, matching
 * `.local/usage-limits-periods.html`'s `.segmented` pattern: the checked state and the
 * focus ring are both CSS sibling selectors on the input, so the semantics an assistive
 * technology sees (a set of radios, one checked, one focusable at a time) are the real
 * ones — nothing here is a styled `<div>` pretending to be a control.
 */
export function PeriodField({
  value,
  onChange,
  disabled = false,
}: PeriodFieldProps) {
  const name = useId();
  const legendId = `${name}-legend`;

  return (
    <div className="flex flex-col gap-1.5">
      <span id={legendId} className="text-sm font-medium">
        Period
      </span>
      <div
        role="radiogroup"
        aria-labelledby={legendId}
        className="border-border bg-muted/40 flex overflow-hidden rounded-md border"
      >
        {OPTIONS.map((option) => {
          const optionId = `${name}-${option}`;
          return (
            <span key={option} className="relative flex-1">
              <input
                type="radio"
                id={optionId}
                name={name}
                value={option}
                checked={value === option}
                disabled={disabled}
                onChange={() => {
                  onChange(option);
                }}
                className="peer absolute inset-0 opacity-0"
              />
              <label
                htmlFor={optionId}
                className={`peer-checked:bg-card peer-checked:text-primary peer-focus-visible:ring-ring block cursor-pointer rounded-sm px-2 py-1.5 text-center text-sm font-medium whitespace-nowrap transition-colors peer-checked:font-semibold peer-checked:shadow-raised peer-focus-visible:ring-3 peer-focus-visible:outline-none ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:text-foreground text-muted-foreground'}`}
              >
                {PERIOD_LABEL[option]}
              </label>
            </span>
          );
        })}
      </div>
    </div>
  );
}
